import type { AiManagedGroupDetail, AiManagedGroupPolicy } from "@adpilot/shared";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPopup,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const GOALS = [
  "grow_sales",
  "improve_efficiency",
  "launch_product",
  "clear_inventory",
  "explore_and_learn",
  "defend_branded_traffic",
] as const;
const RISKS = ["conservative", "balanced", "aggressive"] as const;
const MODES = ["recommendation", "semi-auto", "full-auto"] as const;

interface EditManagedGroupDialogProps {
  group: AiManagedGroupDetail;
  open: boolean;
  saving: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (value: { name: string; policy: AiManagedGroupPolicy }) => void;
}

export function EditManagedGroupDialog({
  group,
  open,
  saving,
  onOpenChange,
  onSave,
}: EditManagedGroupDialogProps): React.ReactElement {
  const { t } = useTranslation("managedGroups");
  const [name, setName] = useState(group.name);
  const [policy, setPolicy] = useState(group.policy);
  useEffect(() => {
    if (!open) return;
    setName(group.name);
    setPolicy(group.policy);
  }, [group, open]);

  const valid = name.trim().length > 0 && policy.targetAcos >= 0.01 && policy.targetAcos <= 2;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("edit.title")}</DialogTitle>
          <DialogDescription>{t("edit.description")}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-5 px-6 pb-6">
          <div className="col-span-2 space-y-2">
            <Label htmlFor="edit-managed-group-name">{t("create.name")}</Label>
            <Input
              id="edit-managed-group-name"
              value={name}
              maxLength={100}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <PolicySelect
            label={t("policy.businessGoal")}
            value={policy.businessGoal}
            items={GOALS}
            getLabel={(value) => t(`policy.goals.${value}`)}
            onChange={(businessGoal) => setPolicy((current) => ({ ...current, businessGoal }))}
          />
          <div className="space-y-2">
            <Label htmlFor="edit-managed-group-acos">{t("policy.targetAcos")}</Label>
            <Input
              id="edit-managed-group-acos"
              type="number"
              min="1"
              max="200"
              value={Math.round(policy.targetAcos * 100)}
              onChange={(event) =>
                setPolicy((current) => ({
                  ...current,
                  targetAcos: Number(event.target.value) / 100,
                }))
              }
            />
          </div>
          <PolicySelect
            label={t("policy.riskPreference")}
            value={policy.riskPreference}
            items={RISKS}
            getLabel={(value) => t(`policy.risks.${value}`)}
            onChange={(riskPreference) => setPolicy((current) => ({ ...current, riskPreference }))}
          />
          <PolicySelect
            label={t("policy.executionMode")}
            value={policy.executionMode}
            items={MODES}
            getLabel={(value) => t(`policy.modes.${value}`)}
            onChange={(executionMode) => setPolicy((current) => ({ ...current, executionMode }))}
          />
          <p className="col-span-2 text-xs text-muted-foreground">{t("edit.nextRound")}</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("actions.close")}
          </Button>
          <Button disabled={!valid || saving} onClick={() => onSave({ name: name.trim(), policy })}>
            {t("edit.save")}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}

function PolicySelect<T extends string>({
  label,
  value,
  items,
  getLabel,
  onChange,
}: {
  label: string;
  value: T;
  items: readonly T[];
  getLabel: (value: T) => string;
  onChange: (value: T) => void;
}): React.ReactElement {
  const selectItems = items.map((item) => ({ value: item, label: getLabel(item) }));
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Select
        items={selectItems}
        value={value}
        onValueChange={(next) => next && onChange(next as T)}
      >
        <SelectTrigger className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {selectItems.map((item) => (
            <SelectItem key={item.value} value={item.value}>
              {item.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
